import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RequestService } from '@foundation/network/services';
import { NotificationService } from '@foundation/notification';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';

interface CronJob {
	id: string;
	schedule: string;
	command: string;
	comment: string | null;
	rawLine: string;
	logFile: string | null;
}

interface PredefinedCronJob {
	id: string;
	name: string;
	description: string;
	schedule: string;
	commandTemplate: string;
	comment: string;
	requiredParams: string[];
}

interface CronJobRunResult {
	jobId: string;
	success: boolean;
	stdout: string;
	stderr: string;
	returnCode: number;
	durationSeconds: number;
}

@Component({
	selector: 'lib-admin-cronjobs-page',
	imports: [CommonModule, TranslateDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './admin-cronjobs-page.component.html',
})
export class AdminCronjobsPageComponent implements OnInit {
	private _requestService = inject(RequestService);
	private _notificationService = inject(NotificationService);
	private _translationService = inject(TranslationService);

	private _i18n_jobRemoved = this._translationService.prep('Cron job removed.');
	private _i18n_jobAdded = this._translationService.prep('Cron job added.');
	private _i18n_confirmRemove = this._translationService.prep('Are you sure you want to remove this cron job?');
	private _i18n_removeTitle = this._translationService.prep('Remove cron job');
	private _i18n_remove = this._translationService.prep('Remove');
	private _i18n_loadError = this._translationService.prep('Failed to load cron jobs.');
	private _i18n_runFailed = this._translationService.prep('Run failed');
	private _i18n_runFailedDescription = this._translationService.prep('Could not execute the cron job.');
	private _i18n_logsFailed = this._translationService.prep('Failed to load logs');
	private _i18n_logsFailedDescription = this._translationService.prep('Could not fetch log file.');
	private _i18n_removeFailed = this._translationService.prep('Failed to remove job');
	private _i18n_removeFailedDescription = this._translationService.prep('Could not remove the cron job.');
	private _i18n_addFailed = this._translationService.prep('Failed to add job');
	private _i18n_addFailedDescription = this._translationService.prep('Could not add the cron job.');

	isLoading = signal(true);
	loadError = signal<string | null>(null);
	jobs = signal<CronJob[]>([]);
	predefinedJobs = signal<PredefinedCronJob[]>([]);

	// For the "add custom job" form
	showAddForm = signal(false);
	newSchedule = signal('');
	newCommand = signal('');
	newComment = signal('');
	newWithLogFile = signal(false);
	isAdding = signal(false);

	// For the "add predefined job" panel
	showPredefinedPanel = signal(false);
	selectedPredefined = signal<PredefinedCronJob | null>(null);
	predefinedParams = signal<Record<string, string>>({});
	predefinedScheduleOverride = signal('');
	isAddingPredefined = signal(false);
	arePredefinedParamsComplete = computed(() => {
		const predefined = this.selectedPredefined();
		if (!predefined) return false;
		const params = this.predefinedParams();
		return predefined.requiredParams.every((param) => params[param]?.trim());
	});

	// Per-job state
	runningJobId = signal<string | null>(null);
	loadingLogsJobId = signal<string | null>(null);
	deletingJobId = signal<string | null>(null);
	expandedJobId = signal<string | null>(null);
	jobRunResult = signal<CronJobRunResult | null>(null);
	jobLogs = signal<string | null>(null);

	ngOnInit(): void {
		this._loadJobs();
		this._loadPredefinedJobs();
	}

	private _loadJobs(): void {
		this.isLoading.set(true);
		this.loadError.set(null);
		this._requestService.getBasic$<{ jobs: CronJob[] }>('/api/cronjobs').subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.error) {
					this.loadError.set(response.error.title);
					return;
				}
				this.jobs.set(response.result?.jobs ?? []);
			},
			error: () => {
				this.isLoading.set(false);
				this.loadError.set(this._i18n_loadError());
			},
		});
	}

	private _loadPredefinedJobs(): void {
		this._requestService.getBasic$<{ predefinedJobs: PredefinedCronJob[] }>('/api/cronjobs/predefined').subscribe({
			next: (response) => {
				if (!response.error) {
					this.predefinedJobs.set(response.result?.predefinedJobs ?? []);
				}
			},
		});
	}

	refresh(): void {
		this._loadJobs();
	}

	toggleExpandJob(job: CronJob): void {
		if (this.expandedJobId() === job.id) {
			this.closeExpandedJob();
		} else {
			this.expandedJobId.set(job.id);
			this.jobRunResult.set(null);
			this.jobLogs.set(null);
		}
	}

	closeExpandedJob(): void {
		this.expandedJobId.set(null);
		this.jobRunResult.set(null);
		this.jobLogs.set(null);
	}

	runJob(job: CronJob): void {
		if (this.runningJobId()) return;
		this.runningJobId.set(job.id);
		this.expandedJobId.set(job.id);
		this.jobRunResult.set(null);
		this.jobLogs.set(null);

		this._requestService.post$<{ runResult: CronJobRunResult }>(`/api/cronjobs/${job.id}/run`, {}).subscribe({
			next: (response) => {
				this.runningJobId.set(null);
				if (response.error) {
					this._notificationService.snackError(response.error.title, response.error.description ?? '');
					return;
				}
				this.jobRunResult.set(response.result?.runResult ?? null);
			},
			error: () => {
				this.runningJobId.set(null);
				this._notificationService.snackError(this._i18n_runFailed(), this._i18n_runFailedDescription());
			},
		});
	}

	loadLogs(job: CronJob): void {
		if (this.loadingLogsJobId()) return;
		this.loadingLogsJobId.set(job.id);
		this.expandedJobId.set(job.id);
		this.jobRunResult.set(null);

		this._requestService.getBasic$<{ logs: string | null }>(`/api/cronjobs/${job.id}/logs`).subscribe({
			next: (response) => {
				this.loadingLogsJobId.set(null);
				if (response.error) {
					this._notificationService.snackError(response.error.title, response.error.description ?? '');
					return;
				}
				this.jobLogs.set(response.result?.logs ?? null);
			},
			error: () => {
				this.loadingLogsJobId.set(null);
				this._notificationService.snackError(this._i18n_logsFailed(), this._i18n_logsFailedDescription());
			},
		});
	}

	removeJob(job: CronJob): void {
		if (this.deletingJobId()) return;

		this._notificationService
			.confirm(this._i18n_confirmRemove(), this._i18n_removeTitle(), {
				confirmButtonText: this._i18n_remove(),
			})
			.closed.subscribe((confirmed) => {
				if (!confirmed) return;

				this.deletingJobId.set(job.id);
				this._requestService.post$<{ removed: boolean }>(`/api/cronjobs/${job.id}/remove`, {}).subscribe({
					next: (response) => {
						this.deletingJobId.set(null);
						if (response.error) {
							this._notificationService.snackError(response.error.title, response.error.description ?? '');
							return;
						}
						this._notificationService.snackSuccess(this._i18n_jobRemoved());
						this._loadJobs();
					},
					error: () => {
						this.deletingJobId.set(null);
						this._notificationService.snackError(this._i18n_removeFailed(), this._i18n_removeFailedDescription());
					},
				});
			});
	}

	toggleAddForm(): void {
		this.showAddForm.update((v) => !v);
		this.showPredefinedPanel.set(false);
		this.newSchedule.set('');
		this.newCommand.set('');
		this.newComment.set('');
		this.newWithLogFile.set(false);
	}

	addJob(): void {
		if (this.isAdding()) return;
		const schedule = this.newSchedule().trim();
		const command = this.newCommand().trim();
		if (!schedule || !command) return;

		this.isAdding.set(true);
		this._requestService
			.post$<{ job: CronJob }>('/api/cronjobs', {
				schedule,
				command,
				comment: this.newComment().trim() || null,
				withLogFile: this.newWithLogFile(),
			})
			.subscribe({
				next: (response) => {
					this.isAdding.set(false);
					if (response.error) {
						this._notificationService.snackError(response.error.title, response.error.description ?? '');
						return;
					}
					this._notificationService.snackSuccess(this._i18n_jobAdded());
					this.showAddForm.set(false);
					this._loadJobs();
				},
				error: () => {
					this.isAdding.set(false);
					this._notificationService.snackError(this._i18n_addFailed(), this._i18n_addFailedDescription());
				},
			});
	}

	togglePredefinedPanel(): void {
		this.showPredefinedPanel.update((v) => !v);
		this.showAddForm.set(false);
		this.selectedPredefined.set(null);
		this.predefinedParams.set({});
		this.predefinedScheduleOverride.set('');
	}

	selectPredefined(predefined: PredefinedCronJob): void {
		this.selectedPredefined.set(predefined);
		// Initialize param map with empty strings
		const initialParams: Record<string, string> = {};
		for (const param of predefined.requiredParams) {
			initialParams[param] = '';
		}
		this.predefinedParams.set(initialParams);
		this.predefinedScheduleOverride.set(predefined.schedule);
	}

	setPredefinedParam(key: string, value: string): void {
		this.predefinedParams.update((params) => ({ ...params, [key]: value }));
	}

	addPredefinedJob(): void {
		const predefined = this.selectedPredefined();
		if (!predefined || this.isAddingPredefined()) return;

		this.isAddingPredefined.set(true);
		this._requestService
			.post$<{ job: CronJob }>('/api/cronjobs/from-predefined', {
				templateId: predefined.id,
				schedule: this.predefinedScheduleOverride() || null,
				params: this.predefinedParams(),
				withLogFile: true,
			})
			.subscribe({
				next: (response) => {
					this.isAddingPredefined.set(false);
					if (response.error) {
						this._notificationService.snackError(response.error.title, response.error.description ?? '');
						return;
					}
					this._notificationService.snackSuccess(this._i18n_jobAdded());
					this.showPredefinedPanel.set(false);
					this.selectedPredefined.set(null);
					this._loadJobs();
				},
				error: () => {
					this.isAddingPredefined.set(false);
					this._notificationService.snackError(this._i18n_addFailed(), this._i18n_addFailedDescription());
				},
			});
	}

	getParamPlaceholder(param: string): string {
		const hints: Record<string, string> = {
			backend_dir: '/path/to/backend',
			env_file: '/path/to/app.env',
			python_path: '/path/to/backend/.venv/bin/python',
			script_path: '/path/to/script.py',
		};
		return hints[param] ?? param;
	}

	trackByJobId(_index: number, job: CronJob): string {
		return job.id;
	}

	trackByPredefinedId(_index: number, predefined: PredefinedCronJob): string {
		return predefined.id;
	}
}
